import { AlertTriangle } from 'lucide-react'

import type { DealFieldDef } from '@/services/dealFile'
import { formatVarianceFigure } from './format'

export type VarianceDetail = { stated?: unknown; computed?: unknown; implied?: unknown; note?: string | null }

/**
 * The save bar's notice of a stated figure the deal's other figures do not
 * reproduce. Rule R1/R4: shown for information. Nobody picks between two
 * outputs; a wrong INPUT is corrected, and the derived figure follows it.
 *
 * Each figure is printed on the scale the derivation writes it in — points for
 * a percent (see `formatVarianceFigure`) — never through the catalogue's
 * magnitude guess, which read a computed 0.74% cash-on-cash as "73.88%".
 */
export function VarianceNotice({
  entries,
  defs,
}: {
  entries: Array<[string, VarianceDetail | undefined]>
  defs: DealFieldDef[]
}) {
  if (entries.length === 0) return null
  return (
    <div className="mb-2 rounded border border-amber-500/25 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-100">
      <div className="flex items-center gap-1.5 font-medium">
        <AlertTriangle size={12} />
        {entries.length} {entries.length === 1 ? 'variance' : 'variances'} — a
        stated figure differs from what the deal&rsquo;s other figures imply. Shown for
        information; nothing was overwritten. If an input is wrong, correct the input.
      </div>
      <ul className="mt-1 space-y-0.5 pl-5 text-[10px] text-amber-200/80">
        {entries.map(([field, detail]) => {
          const def = defs.find((d) => d.name === field)
          return (
            <li key={field}>
              <span>{def?.label ?? field}</span> — stated {formatVarianceFigure(detail?.stated, def?.kind)}; the
              other figures give {formatVarianceFigure(detail?.computed ?? detail?.implied, def?.kind)}
              {detail?.note ? ` (${detail.note})` : ''}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
